/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { stkAPI, filesAPI } from '../lib/api';
import { formatDateTime, STK_STATUS, formatApiErrorDetail, STK_KURUM_TURU_LABELS } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  ChevronLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  FileText,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Calendar,
  Users,
  Briefcase,
  Loader2,
  Download,
  Send,
  Edit,
} from 'lucide-react';
import { STKEditDialog } from '../components/STKEditDialog';

const STKDetailPage = () => {
  const navigate = useNavigate();
  const { appId } = useParams();
  const { user } = useAuth();
  usePageTitle('STK Detay');
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('genel');
  
  // Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editInitialTab, setEditInitialTab] = useState('genel');
  const [rejectReason, setRejectReason] = useState('');
  const [documentMessage, setDocumentMessage] = useState('');

  const isAdmin = ['admin', 'genel_sekreter'].includes(user?.role);
  const isPrivileged = ['admin', 'genel_sekreter', 'kent_konseyi_baskani'].includes(user?.role);
  const isOwner = application?.created_by === user?.id;
  const canEdit = isPrivileged || isOwner;

  useEffect(() => {
    loadApplication();
  }, [appId]);

  const loadApplication = async () => {
    try {
      const response = await stkAPI.getApplication(appId);
      setApplication(response.data);
    } catch (error) {
      toast.error('Başvuru yüklenirken hata oluştu');
      navigate('/panel/stk');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!confirm('Bu başvuruyu onaylamak istediğinizden emin misiniz?')) return;
    
    setActionLoading(true);
    try {
      await stkAPI.approve(appId);
      toast.success('Başvuru onaylandı');
      loadApplication();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await stkAPI.reject(appId, rejectReason);
      toast.success('Başvuru reddedildi');
      setRejectDialogOpen(false);
      loadApplication();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDocuments = async () => {
    setActionLoading(true);
    try {
      await stkAPI.requestDocuments(appId, documentMessage);
      toast.success('Belge talebi gönderildi');
      setDocumentDialogOpen(false);
      loadApplication();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubmit = async () => {
    if (!window.confirm('Belgelerinizi yuklediginizden emin misiniz? Basvurunuz yeniden inceleme icin gonderilecek.')) return;
    setActionLoading(true);
    try {
      await stkAPI.submitApplication(appId);
      toast.success('Basvurunuz yeniden incelemeye gonderildi');
      loadApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gonderim basarisiz');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadBelge = async (belge) => {
    if (!belge?.id) {
      toast.error('Belge bulunamadı');
      return;
    }
    try {
      const response = await filesAPI.downloadBlob(belge.id);
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = belge.name || `belge-${belge.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error('Belge indirilemedi: ' + (error.response?.status === 404 ? 'Dosya bulunamadı' : 'Sunucu hatası'));
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = STK_STATUS[status] || { label: status, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return <Badge className={`${colorClasses[statusInfo.color]} border text-sm px-3 py-1`}>{statusInfo.label}</Badge>;
  };

  const getStatusIcon = (status) => {
    const icons = {
      taslak: Clock,
      inceleniyor: AlertCircle,
      eksik_belge: AlertCircle,
      onaylandi: CheckCircle,
      reddedildi: XCircle,
    };
    const Icon = icons[status] || Clock;
    const colors = {
      taslak: 'text-slate-500',
      inceleniyor: 'text-blue-500',
      eksik_belge: 'text-amber-500',
      onaylandi: 'text-emerald-500',
      reddedildi: 'text-red-500',
    };
    return <Icon className={`w-6 h-6 ${colors[status] || 'text-slate-500'}`} />;
  };

  const kurumTurleri = STK_KURUM_TURU_LABELS;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!application) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="stk-detail-page">
      {/* Back */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/stk')} className="text-slate-600 hover:text-slate-900">
          <ChevronLeft className="w-4 h-4 mr-1" /> STK Basvurulari
        </Button>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => { setEditInitialTab('genel'); setEditDialogOpen(true); }} data-testid="stk-detail-edit-btn">
            <Edit className="w-4 h-4 mr-1" /> Düzenle
          </Button>
        )}
      </div>

      {/* Header Card */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-teal-800 via-cyan-900 to-teal-800 px-6 py-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{application.kurum_adi}</h1>
                <p className="text-sm text-white/50">{kurumTurleri[application.kurum_turu] || application.kurum_turu}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(application.status)}
            </div>
          </div>
        </div>
      </Card>

      {/* Admin Actions */}
      {isPrivileged && application.status === 'inceleniyor' && (
        <Card className="shadow-sm border-blue-200 bg-blue-50/30">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900">Basvuru Inceleme</h3>
                <p className="text-sm text-slate-600">Bu basvuru hakkinda karar verin</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDocumentDialogOpen(true)} disabled={actionLoading}>
                  <FileText className="w-4 h-4 mr-2" /> Belge Iste
                </Button>
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setRejectDialogOpen(true)} disabled={actionLoading}>
                  <XCircle className="w-4 h-4 mr-2" /> Reddet
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Onayla
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Alerts */}
      {application.status === 'onaylandi' && (
        <Card className="shadow-sm border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-900">Bu basvuru onaylanmistir</p>
                <p className="text-sm text-emerald-700">Onaylayan: Sistem Yoneticisi &bull; {formatDateTime(application.approved_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {application.status === 'reddedildi' && (
        <Card className="shadow-sm border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Bu basvuru reddedilmistir</p>
                {application.rejection_reason && <p className="text-sm text-red-700 mt-1">Sebep: {application.rejection_reason}</p>}
                <p className="text-sm text-red-700">{formatDateTime(application.rejected_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {application.status === 'belge_bekleniyor' && (
        <Card className="shadow-sm border-amber-200 bg-amber-50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">Ek belge istendi</p>
                {application.document_request_message && (
                  <p className="text-sm text-amber-800 mt-1 whitespace-pre-wrap">{application.document_request_message}</p>
                )}
              </div>
            </div>
            {isOwner && (
              <div className="border-t border-amber-200 pt-3 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                <p className="text-sm text-amber-800">Belgelerinizi <strong>Belgeler</strong> sekmesinden yükleyin, ardından yeniden gönderin.</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditInitialTab('belgeler'); setEditDialogOpen(true); }}
                    data-testid="stk-upload-docs-btn"
                  >
                    <FileText className="w-4 h-4 mr-1" /> Belge Yukle
                  </Button>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleResubmit}
                    disabled={actionLoading}
                    data-testid="stk-resubmit-btn"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                    Yeniden Gonder
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pill Tabs */}
      <div className="flex gap-1.5 flex-wrap" data-testid="stk-detail-tabs">
        {[
          { value: 'genel', label: 'Genel Bilgiler' },
          { value: 'iletisim', label: 'Iletisim' },
          { value: 'temsilci', label: 'Temsilci' },
          { value: 'yonetim', label: 'Yonetim Kurulu' },
          { value: 'faaliyet', label: 'Faaliyet' },
          { value: 'belgeler', label: 'Belgeler' },
        ].map(t => (
          <button
            key={t.value}
            onClick={() => setActiveTab(t.value)}
            data-testid={`stk-dtab-${t.value}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === t.value
                ? 'bg-teal-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'genel' && (
        <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Kurumsal Bilgiler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Kurum Adı</p>
                    <p className="font-medium text-slate-900">{application.kurum_adi}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Kısa Adı</p>
                    <p className="font-medium text-slate-900">{application.kurum_kisa_adi || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Kurum Türü</p>
                    <p className="font-medium text-slate-900">{kurumTurleri[application.kurum_turu] || '-'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Kuruluş Tarihi</p>
                    <p className="font-medium text-slate-900">{application.kurulus_tarihi || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Sicil No</p>
                    <p className="font-medium text-slate-900">{application.sicil_no || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Vergi No</p>
                    <p className="font-medium text-slate-900">{application.vergi_no || '-'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
      )}

      {activeTab === 'iletisim' && (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>İletişim Bilgileri</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Adres</p>
                    <p className="font-medium text-slate-900">{application.adres || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Telefon</p>
                    <p className="font-medium text-slate-900">{application.telefon || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">E-posta</p>
                    <p className="font-medium text-slate-900">{application.email || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Web Sitesi</p>
                    <p className="font-medium text-slate-900">
                      {application.web_sitesi ? (
                        <a href={application.web_sitesi} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {application.web_sitesi}
                        </a>
                      ) : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
      )}

      {activeTab === 'temsilci' && (
        <div className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Asıl Temsilci Bilgileri</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Ad Soyad</p>
                      <p className="font-medium text-slate-900">
                        {application.temsilci_adi} {application.temsilci_soyadi}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Görevi</p>
                      <p className="font-medium text-slate-900">{application.temsilci_gorevi || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">TC Kimlik</p>
                      <p className="font-medium text-slate-900">{application.temsilci_tc || '-'}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Telefon</p>
                      <p className="font-medium text-slate-900">{application.temsilci_telefon || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">E-posta</p>
                      <p className="font-medium text-slate-900">{application.temsilci_email || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {(application.yedek_temsilci_adi || application.yedek_temsilci_soyadi || application.yedek_temsilci_email) && (
            <Card className="shadow-sm border-amber-200 bg-amber-50/30">
              <CardHeader>
                <CardTitle className="text-base">Yedek Temsilci Bilgileri</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Ad Soyad</p>
                        <p className="font-medium text-slate-900">
                          {application.yedek_temsilci_adi || '-'} {application.yedek_temsilci_soyadi || ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Görevi</p>
                        <p className="font-medium text-slate-900">{application.yedek_temsilci_gorevi || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">TC Kimlik</p>
                        <p className="font-medium text-slate-900">{application.yedek_temsilci_tc || '-'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Telefon</p>
                        <p className="font-medium text-slate-900">{application.yedek_temsilci_telefon || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">E-posta</p>
                        <p className="font-medium text-slate-900">{application.yedek_temsilci_email || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'yonetim' && (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Yönetim Kurulu</CardTitle>
            </CardHeader>
            <CardContent>
              {application.yonetim_kurulu && application.yonetim_kurulu.length > 0 ? (
                <div className="space-y-3">
                  {application.yonetim_kurulu.map((member, index) => (
                    <div key={`yk-${index}`} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-purple-600">
                          {member.ad?.charAt(0)}{member.soyad?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{member.ad} {member.soyad}</p>
                        <p className="text-sm text-slate-500">{member.gorev || 'Üye'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Yönetim kurulu bilgisi girilmemiş</p>
              )}
            </CardContent>
          </Card>
      )}

      {activeTab === 'faaliyet' && (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Faaliyet Bilgileri</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{application.uye_sayisi || 0}</p>
                  <p className="text-sm text-slate-500">Üye Sayısı</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Briefcase className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{application.aktif_proje_sayisi || 0}</p>
                  <p className="text-sm text-slate-500">Aktif Proje</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{application.kurulus_tarihi ? new Date().getFullYear() - new Date(application.kurulus_tarihi).getFullYear() : '-'}</p>
                  <p className="text-sm text-slate-500">Yıllık Tecrübe</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Faaliyet Alanı</p>
                  <p className="text-slate-900">{application.faaliyet_alani || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Faaliyet Özeti</p>
                  <p className="text-slate-900 whitespace-pre-wrap">{application.faaliyet_ozeti || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Kent Konseyi Katılım Amacı</p>
                  <p className="text-slate-900 whitespace-pre-wrap">{application.katilim_amaci || '-'}</p>
                </div>
                {application.katki_alanlari && application.katki_alanlari.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Katkı Alanları</p>
                    <div className="flex flex-wrap gap-2">
                      {application.katki_alanlari.map((alan, index) => (
                        <Badge key={`katki-${index}`} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {alan}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
      )}

      {activeTab === 'belgeler' && (
        <>
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Yüklenen Belgeler</CardTitle>
            </CardHeader>
            <CardContent>
              {application.belgeler && application.belgeler.length > 0 ? (
                <div className="space-y-2">
                  {application.belgeler.map((belge, index) => (
                    <div key={`belge-${index}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`belge-row-${index}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div className="flex flex-col">
                          <span className="text-slate-900 text-sm font-medium">{belge.name || `Belge ${index + 1}`}</span>
                          {belge.belge_turu && (
                            <span className="text-xs text-slate-500">
                              {belge.belge_turu === 'derbis' && 'DERBİS Kayıt Belgesi'}
                              {belge.belge_turu === 'yonetim_kurulu_karari' && 'Yönetim Kurulu Kararı'}
                              {belge.belge_turu === 'tuzuk' && 'Tüzük'}
                              {belge.belge_turu === 'genel_kurul_tutanagi' && 'Genel Kurul Tutanağı'}
                              {belge.belge_turu === 'yetkili_temsilci' && 'Yetkili Temsilci Belgesi'}
                              {belge.belge_turu === 'diger' && 'Diğer'}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadBelge(belge)}
                        data-testid={`belge-download-btn-${index}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        İndir
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Henüz belge yüklenmemiş</p>
              )}
            </CardContent>
          </Card>

          {/* Sonradan Talep Edilen Belgeler (KVKK Belge Talep modulu) */}
          {application.extra_documents && application.extra_documents.length > 0 && (
            <Card className="shadow-sm mt-4" data-testid="extra-documents-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-600" />
                  Sonradan Talep Edilen Belgeler
                  <span className="text-xs font-normal text-slate-500">({application.extra_documents.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {application.extra_documents.map((doc, index) => (
                    <div key={doc.item_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-testid={`extra-doc-row-${index}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-amber-600" />
                        <div className="flex flex-col">
                          <span className="text-slate-900 text-sm font-medium">{doc.document_type}</span>
                          <span className="text-xs text-slate-500">
                            Talep: <span className="font-medium">{doc.request_title}</span>
                            {doc.uploaded_at && (
                              <> · Yüklenme: {new Date(doc.uploaded_at).toLocaleDateString('tr-TR')}</>
                            )}
                          </span>
                        </div>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/stk/document-requests/items/${doc.item_id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:underline"
                        data-testid={`extra-doc-download-${index}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        İndir
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Application Meta */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-slate-500">Başvuru Bilgileri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Oluşturulma</p>
              <p className="font-medium">{formatDateTime(application.created_at)}</p>
            </div>
            <div>
              <p className="text-slate-500">Son Güncelleme</p>
              <p className="font-medium">{formatDateTime(application.updated_at)}</p>
            </div>
            {application.submitted_at && (
              <div>
                <p className="text-slate-500">Gönderilme</p>
                <p className="font-medium">{formatDateTime(application.submitted_at)}</p>
              </div>
            )}
            <div>
              <p className="text-slate-500">Tamamlanma</p>
              <p className="font-medium">{Math.round((application.current_step / 9) * 100)}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Başvuruyu Reddet</DialogTitle>
            <DialogDescription>
              Bu işlem geri alınamaz. Başvuru sahibine bildirim gönderilecektir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Red Sebebi</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Red sebebini açıklayın..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Request Dialog */}
      <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eksik Belge Talebi</DialogTitle>
            <DialogDescription>
              Başvuru sahibinden ek belge talep edin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Mesaj</Label>
              <Textarea
                value={documentMessage}
                onChange={(e) => setDocumentMessage(e.target.value)}
                placeholder="İstenen belgeleri ve açıklamaları yazın..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleRequestDocuments} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Talep Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <STKEditDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditInitialTab('genel'); }}
        application={application}
        isOwner={isOwner}
        isPrivileged={isPrivileged}
        initialTab={editInitialTab}
        onSaved={(updated) => setApplication(updated)}
      />
    </div>
  );
};

export default STKDetailPage;
